/**
 * Sessions: finished workouts as stored in the history. Each has per exercise the sets with their type and the
 * values done. Exercises are referenced as `exercises: [{ exerciseId }]` (`exerciseUsage` relies on that), and
 * `templateId` and `endedAt` are read by the template cards ("Zuletzt: …").
 */
import { asEntry, entryMeasure, requiredFields, withMeasure, type Exercise, type Measure, type Variant } from "./exercises";
import type { Backend } from "./storage";
import { cleanSet, cleanTemplate, optionalTargets, type SetType, type TemplateDraft, type TemplateSet } from "./templates";

/** The values of a set; which ones an exercise has says `setFields()`. */
export interface SetValues {
  weightKg?: number;
  reps?: number;
  durationSec?: number;
  distanceM?: number;
}

export const VALUE_KEYS = ["weightKg", "reps", "durationSec", "distanceM"] as const;

export interface SessionSet extends SetValues {
  type: SetType;
  /** ticked; unticked sets are dropped when a workout is finished, so stored sets are all done */
  done: boolean;
}

export interface SessionExercise {
  exerciseId: string;
  /** the variant the exercise was done in (todo 16); absent: the glossary's measure */
  measure?: Variant;
  restSec: number;
  sets: SessionSet[];
}

/** Which session entries count as one exercise: an id, or a test such as `sameVariant`. */
export type EntryMatch = string | ((x: Pick<SessionExercise, "exerciseId" | "measure">) => boolean);

const matcher = (which: EntryMatch) => (typeof which === "string" ? (x: { exerciseId: string }) => x.exerciseId === which : which);

/**
 * The entries of the exercise done in the given variant (todo 16: "Vorher", history, records and charts compare only
 * the same variant). Entries without a variant are the glossary's measure.
 */
export const sameVariant = (e: Pick<Exercise, "id" | "measure">, measure: Measure = e.measure) =>
  (x: Pick<SessionExercise, "exerciseId" | "measure">) => x.exerciseId === e.id && entryMeasure(e, x) === measure;

export interface Session {
  id: string;
  name: string;
  /** the template the workout was started from, if any (the template may be deleted since) */
  templateId?: string;
  /** milliseconds since the epoch */
  startedAt: number;
  endedAt: number;
  notes: string;
  exercises: SessionExercise[];
}

/** The sets of the exercise in the latest session that has it (its first occurrence there), for "Vorher". */
export function lastSessionSets(sessions: Session[], which: EntryMatch): SessionSet[] | undefined {
  const match = matcher(which);
  let best: Session | undefined;
  for (const s of sessions) {
    if (s.exercises.some(match) && (!best || s.endedAt > best.endedAt)) best = s;
  }
  return best?.exercises.find(match)?.sets;
}

/**
 * "Vorher" of set i: the set of the same type at the same position among that type last time (the second normal
 * set is compared with the second normal set, warm-ups with warm-ups).
 */
export function previousSet(previous: SessionSet[] | undefined, sets: { type: SetType }[], i: number): SessionSet | undefined {
  if (!previous || !sets[i]) return undefined;
  const type = sets[i].type;
  const n = sets.slice(0, i).filter((s) => s.type === type).length;
  return previous.filter((s) => s.type === type)[n];
}

/** Sum of weight × reps over the done sets, warm-ups left out (they never count for records or trends). */
export function volume(exercises: { sets: (SetValues & { type: SetType; done: boolean })[] }[]): number {
  let sum = 0;
  for (const x of exercises) {
    for (const s of x.sets) {
      if (s.done && s.type !== "warmup" && s.weightKg !== undefined && s.reps !== undefined) sum += s.weightKg * s.reps;
    }
  }
  return Math.round(sum * 100) / 100;
}

// --- storage ---

export const listSessions = (db: Backend) => db.getAll<Session>("sessions");
export const saveSession = (db: Backend, s: Session) => db.put("sessions", s);
export const deleteSession = (db: Backend, id: string) => db.delete("sessions", id);

// --- history list ---

/** Newest first, grouped by calendar month (local time). `key` is "2026-10". */
export function groupByMonth(sessions: Session[]): { key: string; year: number; month: number; items: Session[] }[] {
  const sorted = [...sessions].sort((a, b) => b.endedAt - a.endedAt || b.id.localeCompare(a.id));
  const groups: { key: string; year: number; month: number; items: Session[] }[] = [];
  for (const s of sorted) {
    const d = new Date(s.startedAt);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const last = groups[groups.length - 1];
    if (last?.key === key) last.items.push(s);
    else groups.push({ key, year: d.getFullYear(), month: d.getMonth(), items: [s] });
  }
  return groups;
}

// --- editing a finished session ---

export type SessionError = "valueInvalid" | "valueMissing" | "timeInvalid" | "empty";

/**
 * What gets stored from an edited session: trimmed text, every set cleaned against its exercise and marked done (a
 * stored set is always done), exercises without sets dropped.
 */
export function cleanSession(s: Session, exercises: Exercise[]): Session {
  const byId = new Map(exercises.map((e) => [e.id, e]));
  return {
    ...s,
    name: s.name.replace(/\s+/g, " ").trim(),
    notes: s.notes.trim(),
    exercises: s.exercises
      .map((x) => {
        const e = byId.get(x.exerciseId);
        return withMeasure({ ...x, sets: x.sets.map((set) => ({ ...cleanSet(set, asEntry(e, x)), done: true })) }, e, x.measure);
      })
      .filter((x) => x.sets.length > 0),
  };
}

/** Errors of an edited session; `missing` lists the sets without the measure's main value (exercise, set index). */
export function validateSession(s: Session, exercises: Exercise[]): { errors: SessionError[]; missing: [number, number][] } {
  const byId = new Map(exercises.map((e) => [e.id, e]));
  const errors: SessionError[] = [];
  const missing: [number, number][] = [];
  if (!(s.endedAt >= s.startedAt) || !Number.isFinite(s.startedAt)) errors.push("timeInvalid");
  const values = s.exercises.flatMap((x) => x.sets.flatMap((set) => VALUE_KEYS.map((k) => set[k])));
  if (values.some((v) => v !== undefined && !(Number.isFinite(v) && v >= 0))) errors.push("valueInvalid");
  s.exercises.forEach((x, i) => x.sets.forEach((set, j) => {
    if (requiredFields(asEntry(byId.get(x.exerciseId), x)).some((k) => set[k] === undefined)) missing.push([i, j]);
  }));
  if (missing.length) errors.push("valueMissing");
  if (!s.exercises.some((x) => x.sets.length > 0)) errors.push("empty");
  return { errors, missing };
}

export type SaveSessionResult = { ok: true; session: Session } | { ok: false; errors: SessionError[]; missing: [number, number][] };

/** Stores an edited session after validating and cleaning it. An empty name keeps the old one. */
export async function updateSession(db: Backend, edited: Session, exercises: Exercise[]): Promise<SaveSessionResult> {
  const v = validateSession(edited, exercises);
  if (v.errors.length) return { ok: false, ...v };
  const old = await db.get<Session>("sessions", edited.id);
  const session = cleanSession(edited, exercises);
  if (!session.name) session.name = old?.name ?? edited.name;
  await saveSession(db, session);
  return { ok: true, session };
}

/**
 * A template draft from a session ("als Template speichern", "Wiederholen"): the exercises in order with their rest
 * and the sets with the values done. The optional targets (Ziel-Reps of a timed set, Ziel-Zeit of a distance set) are
 * left empty: a session only has values, no goals (the same as "Vorlage aktualisieren" keeps goals, not values).
 */
export function sessionTemplate(s: Session, exercises: Exercise[], name = s.name): TemplateDraft {
  const byId = new Map(exercises.map((e) => [e.id, e]));
  const draft: TemplateDraft = {
    name, notes: "",
    exercises: s.exercises.map((x) => {
      const e = asEntry(byId.get(x.exerciseId), x);
      const opt = e ? optionalTargets(e) : { reps: false, duration: false };
      return {
        exerciseId: x.exerciseId, ...(x.measure ? { measure: x.measure } : {}), restSec: x.restSec,
        sets: x.sets.map(({ done: _, ...set }) => {
          const out: TemplateSet = { ...set };
          if (opt.reps) delete out.reps;
          if (opt.duration) delete out.durationSec;
          return out;
        }),
      };
    }),
  };
  return cleanTemplate(draft, exercises);
}
